import Vue from 'vue'
import VueRouter from 'vue-router'
import LoginView from '../views/LoginView.vue'
import RestaurantSelectionView from '../views/RestaurantSelectionView.vue'
import StationSelectionView from '../views/StationSelectionView.vue'
import OrderDisplayView from '../views/OrderDisplayView.vue'
import OrderOverviewView from '../views/OrderOverviewView.vue'
import store from '../store'

Vue.use(VueRouter)

const routes = [
  ...(process.env.NODE_ENV !== 'production' ? [{ path: '/preview', name: 'preview', component: () => import('../views/PreviewView.vue') }] : []),
  {
    path: '/login',
    name: 'login',
    component: LoginView
  },
  {
    path: '/',
    name: 'restaurant-selection',
    component: RestaurantSelectionView,
    meta: { requiresAuth: true }
  },
  {
    path: '/restaurant-selection',
    redirect: '/'
  },
  {
    path: '/station-selection',
    name: 'station-selection',
    component: StationSelectionView,
    meta: { requiresAuth: true }
  },
  {
    path: '/orders',
    name: 'order-display',
    component: OrderDisplayView,
    meta: { requiresAuth: true }
  },
  {
    path: '/order-overview',
    name: 'order-overview',
    component: OrderOverviewView,
    meta: { requiresAuth: true }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// Track if Firebase auth has been initialized
let authInitialized = false
let authInitPromise = null

// Function to wait for Firebase auth to initialize
function waitForAuthInit() {
  if (authInitialized) return Promise.resolve()

  if (!authInitPromise) {
    authInitPromise = new Promise((resolve) => {
      // Check if already initialized
      if (store.state.firebaseUser !== null) {
        authInitialized = true
        resolve()
        return
      }

      // Set up a watcher for auth state changes
      const unwatch = store.watch(
        (state) => state.firebaseUser,
        (newVal) => {
          if (newVal !== null) {
            authInitialized = true
            unwatch()
            resolve()
          }
        }
      )

      // Also resolve after timeout if localStorage has auth (Firebase may not restore user if session expired)
      setTimeout(() => {
        authInitialized = true
        unwatch()
        resolve()
      }, 2000) // 2 seconds max wait
    })
  }

  return authInitPromise
}

// Authentication guard
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isAuthenticatedInLocalStorage = localStorage.getItem('authenticated') === 'true'

  // Wait for Firebase auth to initialize if we have stored auth
  if (requiresAuth && isAuthenticatedInLocalStorage && !authInitialized) {
    await waitForAuthInit()
  }

  // Re-check auth state after waiting
  const isAuthenticated = store.state.isAuthenticated || isAuthenticatedInLocalStorage
  const hasFirebaseUser = store.state.firebaseUser !== null

  // User is authenticated if:
  // 1. They have a Firebase user session, OR
  // 2. They're in localStorage AND Firebase hasn't explicitly signed them out
  const isFullyAuthenticated = isAuthenticated && (hasFirebaseUser || isAuthenticatedInLocalStorage)

  if (requiresAuth && !isFullyAuthenticated) {
    // Redirect to login if authentication is required but user is not authenticated
    next('/login')
  } else if ((to.path === '/' || to.path === '/restaurant-selection') && isAuthenticated) {
    // Skip restaurant selection and go directly to station selection
    next('/station-selection')
  } else {
    // Proceed normally (including /login page)
    next()
  }
})

export default router
